import { UserDefinedNotificationConfig } from '@apis/Notification/model';
import { QueryDatasourceMetadata } from '@root/Services/Query/QueryDatasource';
import { QueryDatasourceCollection } from '@root/Services/Query/QueryDatasourceCollection';
import { inject, injectable } from 'tsyringe';
import { IDatasourcePresetsBuilder, IDatasourcePresetsBuilderFactory, INotificationPresetItem } from './BasePresets';
import { CostForecastDatasourcePresetsBuilderFactory } from './CostForecastPresets';
import { InvoiceDatasourcePresetsBuilderFactory } from './InvoicePresets';
import { ResourceDatasourcePresetsBuilderFactory } from './ResourcePresets';

class EmptyDatasourcePresetsBuilder implements IDatasourcePresetsBuilder {
    public static readonly instance = new EmptyDatasourcePresetsBuilder();
    public getPresets() {
        return [];
    }
}

@injectable()
export class DatasourcePresetsCollection {
    private readonly factoryByDsName = new Map<string, IDatasourcePresetsBuilderFactory>();

    public constructor(
        @inject(InvoiceDatasourcePresetsBuilderFactory) invoicePresets: InvoiceDatasourcePresetsBuilderFactory,
        @inject(ResourceDatasourcePresetsBuilderFactory) resourcePresets: ResourceDatasourcePresetsBuilderFactory,
        @inject(CostForecastDatasourcePresetsBuilderFactory) costForecastPresets: CostForecastDatasourcePresetsBuilderFactory,
        @inject(QueryDatasourceCollection) private readonly datasources: QueryDatasourceCollection
    ) {
        const factories = [invoicePresets, resourcePresets, costForecastPresets];
        for (const factory of factories) {
            for (const ds of factory.getSupportedDatasources()) {
                this.factoryByDsName.set(ds.name, factory);
            }
        }
    }

    private getDatasourceFactories(datasource?: string): Array<[string, IDatasourcePresetsBuilderFactory]> {
        return datasource ? [[datasource, this.factoryByDsName.get(datasource)!]] : [...this.factoryByDsName.entries()];
    }

    public async getBuilders(datasourceName: string | undefined) {
        const factories = this.getDatasourceFactories(datasourceName);
        return await Promise.all(
            factories.map(async ([ds, f]) => {
                const datasource = await this.datasources.getDatasource(ds, {});
                return !datasource ? EmptyDatasourcePresetsBuilder.instance : await f.get(ds);
            })
        );
    }
}
